import { useEffect, useRef, useState } from 'react';
import { EllipsisHorizontalIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getDashboard, mintRenderUrl } from '../../services/dashboards';
import { useZafronBridge } from './useZafronBridge';

const RANGES = ['1h', '6h', '24h', '3d', '7d', '30d', '90d'];

export function ZafronDashboard({ slug, className, home }) {
  const frameRef = useRef(null);
  const [dashboard, setDashboard] = useState(null);
  const [src, setSrc] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    let cancelled = false;
    setDashboard(null);
    setSrc(null);
    setLoadError(null);
    // Both at once: the nonce does not depend on the record, and the frame
    // cannot render without either.
    Promise.all([getDashboard(slug), mintRenderUrl(slug)])
      .then(([d, minted]) => {
        if (cancelled) return;
        setDashboard(d);
        setSrc(minted.url);
      })
      .catch((e) => { if (!cancelled) setLoadError(e); });
    return () => { cancelled = true; };
  }, [slug]);

  const { ready, busy, height, frameError, dismissError, refresh, onFrameLoad } =
    useZafronBridge({ frameRef, dashboard, timeRange, theme: 'light' });

  // The page draws no heading of its own, so the tab carries the name.
  const title = dashboard?.title;
  useEffect(() => {
    if (!title) return undefined;
    const previous = document.title;
    document.title = title;
    return () => { document.title = previous; };
  }, [title]);

  if (loadError) {
    return (
      <div className={`p-6 text-gray-700 dark:text-gray-300 ${className || ''}`} role="alert">
        <p className="font-medium text-gray-900 dark:text-gray-50">This dashboard could not be loaded.</p>
        <p>
          {loadError.status === 404
            ? 'It was deleted, or you do not have access.'
            : loadError.status === 503
              ? 'The render service is not configured.'
              : loadError.error?.message || loadError.message || 'Something went wrong.'}
        </p>
      </div>
    );
  }

  if (!dashboard || !src) {
    return (
      <div className={`p-6 text-gray-500 dark:text-gray-400 ${className || ''}`} aria-busy="true">
        Loading dashboard…
      </div>
    );
  }

  return (
    <div className={className} data-ready={ready} data-busy={busy > 0}>
      {/* Host chrome lives outside the frame, so the dashboard's HTML never
          has to implement it and never can fake it. It floats in a corner
          rather than taking a bar, so it cannot fight the author's layout. */}
      <FloatingControls home={home}>
        <label htmlFor="zafron-range" className="sr-only">Time range</label>
        <select
          id="zafron-range"
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="rounded-md border-gray-300 py-1 pl-2 pr-8 text-sm text-gray-700 focus:border-blue-600 focus:ring-blue-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
        >
          {RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button
          type="button"
          onClick={refresh}
          disabled={!ready}
          className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          Refresh
        </button>
      </FloatingControls>

      {frameError && (
        <div role="status" className="fixed left-1/2 top-3 z-30 flex max-w-[calc(100%-6rem)] -translate-x-1/2 items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 shadow-lg dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          <span className="min-w-0 flex-1">This dashboard reported an error: {frameError}</span>
          <button type="button" onClick={dismissError} className="font-medium hover:underline">Dismiss</button>
        </div>
      )}

      <iframe
        ref={frameRef}
        src={src}
        title={dashboard.title}
        onLoad={onFrameLoad}
        /* allow-scripts ONLY. Adding allow-same-origin defeats the sandbox and
           hands every viewer's session to whoever authored the page. */
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        style={{ display: 'block', width: '100%', height, border: 0, background: 'transparent' }}
      />
    </div>
  );
}

/**
 * A corner button that expands into the host controls. Collapsed, it covers
 * one small square of the author's page; it is faint until pointed at so it
 * reads as the host's, not the dashboard's.
 */
function FloatingControls({ home, children }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onPointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    // A click inside the frame never reaches this document; it blurs the
    // window instead, which is the same "clicked elsewhere".
    const onBlur = () => setOpen(false);
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('blur', onBlur);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="fixed right-3 top-3 z-30">
      {open ? (
        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white/95 p-1.5 shadow-lg backdrop-blur dark:border-gray-800 dark:bg-gray-900/95">
          {home}
          {children}
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Hide dashboard controls"
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Show dashboard controls"
          className="rounded-lg border border-gray-200 bg-white/70 p-1.5 text-gray-600 opacity-60 shadow-sm backdrop-blur transition hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:border-gray-800 dark:bg-gray-900/70 dark:text-gray-400"
        >
          <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
